/**
 * The Tech 1-4 picker's option list, shared by both ticket editors.
 *
 * THE JOB CREW IS THE DEFAULT LIST, NOT A CLOSED ONE. A hand picked up
 * mid-shift is on the ticket's line items before anyone has put them on the
 * job's crew roster, and the office cannot bill their hours until they can be
 * named. So the picker lists the job crew first, in the roster's own order,
 * and can list every other active employee at the company under a second
 * heading. The API accepts any user at the tenant on a tech slot
 * (tickets.routes.ts checks company ownership, employee routes pass an
 * explicit id through), so nothing downstream needs the person to be on the
 * crew.
 *
 * BUT THE SECOND HEADING IS NOT SHOWN UNTIL ASKED FOR. PR #516 listed the
 * whole company on every open, and a hand at TNT looking for three names was
 * scrolling 49. The editors hold a "show everyone" switch, off on every open;
 * until it is on, the picker is the job crew, plus whoever a slot already
 * holds (see pickerListsForSlot). The switch is also what turns on the
 * GET /users fetch, so the default open never pays for the roster.
 *
 * Two shapes reach this file and both are read:
 *   - crew rows from GET /my-jobs/:id, GET /tickets/:id and the my-tickets
 *     payload: snake_case (`first_name`, `last_name`, `initials`, `name`)
 *   - GET /users rows: camelCase (`firstName`, `lastName`, `fullName`)
 */
import { crewId, crewName, type CrewMemberLike } from './crew-slot';

/** A GET /users row, or a crew row, as the picker reads it. */
export function toCrewMember(u: any): CrewMemberLike & { id: string } {
  return {
    id: String(u?.id ?? u?.userId ?? ''),
    userId: u?.userId ?? null,
    first_name: u?.first_name ?? u?.firstName ?? null,
    last_name: u?.last_name ?? u?.lastName ?? null,
    initials: u?.initials ?? null,
    name: u?.name ?? u?.fullName ?? null,
  };
}

export interface CrewPickerLists {
  /** The job crew, roster order, ids deduplicated. */
  crew: CrewMemberLike[];
  /** Everyone else at the company, by name. Empty when the list is not loaded. */
  others: CrewMemberLike[];
}

/**
 * Split the company into "on this job" and "everyone else". A user present in
 * both inputs appears once, under the crew — the crew row is the one that
 * carries `role_on_job`, and it is the one the office sees on the roster.
 */
export function buildCrewPickerLists(
  jobCrew: CrewMemberLike[] | null | undefined,
  companyUsers: any[] | null | undefined,
): CrewPickerLists {
  const seen = new Set<string>();
  const crew: CrewMemberLike[] = [];
  for (const c of Array.isArray(jobCrew) ? jobCrew : []) {
    const id = crewId(c);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    crew.push(c);
  }
  const others: CrewMemberLike[] = [];
  for (const u of Array.isArray(companyUsers) ? companyUsers : []) {
    const member = toCrewMember(u);
    if (!member.id || seen.has(member.id)) continue;
    seen.add(member.id);
    others.push(member);
  }
  others.sort((a, b) => crewName(a).localeCompare(crewName(b)));
  return { crew, others };
}

/**
 * A slot can hold an id that is in neither list: the person left the company,
 * or the device is offline and the company list never loaded. The select must
 * still SHOW that value rather than silently displaying the first option while
 * state keeps the real id — so the row's own stored initials become an option.
 * Returns null when the id is already listed or the slot is empty.
 */
export function orphanSlotOption(
  lists: CrewPickerLists,
  id: string | null | undefined,
  initials: string | null | undefined,
): CrewMemberLike | null {
  if (!id) return null;
  const listed = lists.crew.some(c => crewId(c) === id) || lists.others.some(c => crewId(c) === id);
  if (listed) return null;
  return { id, initials: initials || null, name: null };
}

/**
 * What one slot's <select> lists. With the switch off, the company list is
 * cut to the person this slot already holds — so a hand picked earlier from
 * the wider list keeps their name on screen after the list folds away, and
 * the select never silently shows the first option while state keeps the
 * real id. With the switch on, the whole company.
 */
export function pickerListsForSlot(
  lists: CrewPickerLists,
  showEveryone: boolean,
  heldId: string | null | undefined,
): CrewPickerLists {
  if (showEveryone) return lists;
  return { crew: lists.crew, others: heldId ? lists.others.filter(c => crewId(c) === heldId) : [] };
}

/** Everyone the picker can name, for resolving a picked id back to a member. */
export function allPickable(lists: CrewPickerLists): CrewMemberLike[] {
  return [...lists.crew, ...lists.others];
}
